/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * useVoiceConnection.ts — localized lifecycle coordinator for call media.
 * It starts the configured transport, updates shared voice state, and performs
 * a single WebSocket fallback after WebRTC failure. Signaling protocols and
 * media implementation details remain isolated in transport classes.
 */

/**
 * E3-S2: Hook to start/stop voice media (WebSocket or WebRTC) when accepting or ending a call.
 * E3-S2 T2: Passes preferred input/output from Settings.
 * E3-S2 T3: On WebRTC error — single automatic fallback to WebSocket + user-visible toasts.
 * E3-S3 T3: Updates voiceConnectionState in store and shows toasts for session/errors.
 */
import { useRef, useCallback, useEffect } from 'react';
import { appConfig, getOperatorId } from '@/config/env';
import { featureFlags } from '@/config/featureFlags';
import { useCall } from '@/store';
import { operatorStore } from '@/store/operatorStore';
import { addToast } from '@/store/toastStore';
import { getPreferredInputConstraints, getPreferredOutputId } from './useAudioDevices';
import { createVoiceTransport } from './voiceTransportFactory';
import { useTranslation } from '@/i18n';

export function useVoiceConnection() {
  const t = useTranslation();
  const transportRef = useRef<ReturnType<typeof createVoiceTransport> | null>(null);
  const call = useCall();
  const fallbackTriedRef = useRef(false);

  const endVoice = useCallback(() => {
    const t = transportRef.current;
    if (t) {
      t.endCall();
      transportRef.current = null;
    }
    fallbackTriedRef.current = false;
  }, []);

  const startVoice = useCallback(
    (params: { callId: string; sessionId?: string | null }) => {
      endVoice();
      const url = appConfig.wsCallUrl;
      if (!url) return;
      const tryConnect = (useWebSocketFallback = false) => {
        transportRef.current = null;
        const transportType =
          useWebSocketFallback || featureFlags.callTransport === 'websocket'
            ? 'websocket'
            : featureFlags.callTransport;
        if (useWebSocketFallback) {
          addToast(t('voice.trying_fallback'));
        }
        const transport = createVoiceTransport(url, transportType, {
          onState: (state) => {
            operatorStore.getState().setVoiceConnectionState(state);
          },
          onSessionStarted: () => {
            addToast(t('voice.connected'));
          },
          onError: (msg) => {
            console.warn('[voice]', transportType, msg);
            const localizedMessage =
              msg === 'voice.invite_timeout' ? t('voice.invite_timeout') : msg;
            addToast(t('voice.error', { message: localizedMessage }));
            if (!fallbackTriedRef.current && featureFlags.callTransport === 'webrtc') {
              fallbackTriedRef.current = true;
              tryConnect(true);
            }
          },
        });
        transportRef.current = transport;
        transport.connect({
          calleeId: getOperatorId(),
          callId: params.callId,
          sessionId: params.sessionId ?? undefined,
          preferredInputConstraints: getPreferredInputConstraints(),
          preferredOutputId: getPreferredOutputId(),
        });
      };
      tryConnect();
    },
    [endVoice, t]
  );

  // Tear down voice when call ends (from server or user).
  useEffect(() => {
    if (call.phase === 'ended') {
      endVoice();
    }
  }, [call.phase, endVoice]);

  return { startVoice, endVoice };
}
